import { action, computed, observable } from 'mobx';
import { observer } from 'mobx-react';
import React from 'react';

import { DbKey, makeWithDbId, WithDbId } from '../../../../../adl-gen/common/db';
import { makePair, Pair } from '../../../../../adl-gen/sys/types';
import {
  AnswerIndex,
  AnswerScores,
  Competency,
  I18nContent,
  makeInternationalized,
  makeMultiResponse,
  makeOrderedResponse,
  makeQuestion,
  makeSingleResponse,
  Question,
  QuestionCategory,
  Score,
  Skill
} from '../../../../../adl-gen/totalinteraction/futurefit/db';
import {
  Entry,
  RepositoryEntries,
  RepositoryEntrySet
} from '../../../../../adl-gen/totalinteraction/futurefit/frontend';
import { RepositoryPutEntry } from '../../../../../adl-gen/totalinteraction/futurefit/requests';
import { LangCode, withI18n, WithI18nProps } from '../../../../../internationalization/i18n-hoc';
import { i18next } from '../../../../../internationalization/i18next';
/// See comment below - QuizQuestion pulls in too many page-layout and other items to use directly
// import { QuizQuestion } from "../../../../ui/screens/quiz-question-screen/quiz-question-screen"
import { CloseIcon } from '../../../../../ui/common/icons';
/// --- [ NB This is the main DnDContainer class used in the main UI ] ---
///  Not the modified one used by the Admin UI, ie.:
// import { DnDContainer } from "../../drag-and-drop/drag-and-drop-container"
/// --- [ TODO Merge them ] ---
import { DnDContainer, DraggableOption } from '../../../../../ui/components/drag-and-drop/drag-and-drop-container';
import { Button } from '../../../../../ui/widgets/button/button';
import { CheckItem } from '../../../../../ui/widgets/check-item/check-item';
// import { SimpleMDEReactContainer } from "../../../../ui/widgets/easy-markdown-editor/easy-markdown-editor"
import { MarkdownContent } from '../../../../../ui/widgets/markdown-content/markdown-content';
import { RadioItem } from '../../../../../ui/widgets/radio-item/radio-item';
import { SelectBox, SelectOption } from '../../../../../ui/widgets/select/select';
import { newNonEmptyTextField } from '../../../../../ui/widgets/text-input/text-field';
import * as FileUtility from '../../../../../util/file-repos-utility';
import { uniqueId, WithId } from '../../../../../util/utils';
import { QuestionCompetencySkillsMap } from '../../../../stores';
import { fakeCallback } from '../../../../stores/quiz-admin-store';
import {
  AnswerType,
  deleteHandler,
  I18nInput,
  I18nMarkdownEditorObserver,
  InputAnswer,
  isEmpty,
  renderButtons,
  /// Replaced with SelectBox, because not responding to actions, BUT maybe just need to
  /// wrap SelectObserver in observer()...
  // SelectObserver,
  TextInputObserver
} from '../../../common/editor';
import { TabContainer } from '../../tab-container/tab-container';
// import { SelectedFileSpec } from "../../image-file-manager/image-file-manager"
import { AnswersEditor } from '../answers-scoring-editors/answers-editor';
import { MultiResponseEditor } from '../answers-scoring-editors/multi-response-scores-editor';
import { OrderedResponseEditor } from '../answers-scoring-editors/ordered-response-editor';
import { SingleResponseEditor } from '../answers-scoring-editors/single-response-scores-editor';

const styles = require('../../../common/editor.css');
// const fonts = require("../../../../ui/common/fonts.css")
const questionScreenStyles = require('../../../../../ui/screens/quiz-question-screen/quiz-question-screen.css');

interface RestrictedFields {}

interface QuestionAnswerEditorProps extends WithI18nProps {
  debug?: boolean;
  /// If skill selected in filter on QuestionList it will show up here
  /// => add new question to skill
  skill?: WithDbId<Skill>;
  /// Edit: specified question
  item?: WithDbId<Question>;
  /// Use to get Skill name or other attributes if
  ///  * item specified (Edit)
  ///  * (New) - no skill specified and using filters
  competencySkillsMap: QuestionCompetencySkillsMap;
  // S3 repository for Question images
  repository: RepositoryEntrySet;
  onCreate(item: Question): Promise<void>;
  onUpdate?(item: WithDbId<Question>): Promise<void>;
  // Upload images to S3
  onUpload(req: RepositoryPutEntry): Promise<Entry>;
  onCancel(): void;
}

/**
 * Component to maintain Questions.
 */
@observer
class QuestionAnswerEditor0 extends React.Component<QuestionAnswerEditorProps> {
  @observable name = newNonEmptyTextField();
  @observable skill?: WithDbId<Skill> | null;
  @observable category: QuestionCategory;
  @observable content: I18nInput = makeInternationalized({ en: newNonEmptyTextField() });
  /// Cached posted updates or existing values
  @observable answers: InputAnswer<I18nContent>[] = [];
  @observable scoring: AnswerScores = {
    kind: 'singleResponse', // default - majority
    value: makeSingleResponse({
      scores: [],
      defaultScore: 33 // default - majority
    })
  };

  // If filters are used (ie. no skill passed in properties)
  @observable filterCompetencyId?: DbKey<Competency>;
  @observable competencyOptions: SelectOption[];
  @observable skillOptions: SelectOption[];
  @observable questionCategoryOptions: SelectOption[];
  // @observable selectedSkillId?: DbKey<Skill>
  showFilters: boolean = false;

  restrictedFields: RestrictedFields = {};

  noShowFields = {};

  @observable lang: LangCode;

  @observable locked = false;

  nonSkillCategoryAnswerTypeOptions: SelectOption[] = [
    { value: 'submitted', label: 'Submitted Responses' },
    { value: 'embedded', label: 'Embedded Training Offering' }
  ];

  skillCategoryAnswerTypeOptions: SelectOption[] = [
    { value: 'singleResponse', label: 'Single Response' },
    { value: 'multiResponse', label: 'Multiple Responses' },
    { value: 'orderedResponse', label: 'Ordered Responses' }
  ];

  @observable answerTypeOptions: SelectOption[] = [
    ...this.skillCategoryAnswerTypeOptions,
    ...this.nonSkillCategoryAnswerTypeOptions
  ];

  constructor(props: Readonly<QuestionAnswerEditorProps>) {
    super(props);
    const { currentLangCode, initI18nEmpty, initI18nFromInput, item, skill } = props;
    this.lang = currentLangCode();
    this.competencyOptions = this.setCompetencyOptions();
    this.skillOptions = this.setSkillOptions();
    this.questionCategoryOptions = this.setQuestionCategoryOptions();
    if (item) {
      this.category = item.value.category;
      this.skill = item.value.skillId ? this.skillFromId(item.value.skillId) : null;
      this.name.onChange(item.value.name);
      initI18nFromInput(this.content, item.value.content);
      // --- [ TODO ] ---------------------------------------------------
      // 1. Not-Applicable questions, for OrderedResponse answer types,
      //    where the answer is 'N/A' and carries a zero score and is the
      //    last answer in the list of answers
      // 2. Limit multiResponses so that the Next button is disabled if
      //    the incorrect number checked
      // ----------------------------------------------------------------
      this.answers = item.value.answers.map(answer => ({
        isNotApplicableAnswer: false,
        answer: { id: uniqueId(), value: answer }
      }));
      switch (item.value.scoring.kind) {
        case 'singleResponse':
          {
            this.scoring = {
              kind: item.value.scoring.kind,
              value: makeSingleResponse({
                scores: item.value.scoring.value.scores,
                defaultScore: item.value.scoring.value.defaultScore
              })
            };
          }
          break;
        case 'multiResponse':
          {
            this.scoring = {
              kind: item.value.scoring.kind,
              value: makeMultiResponse({
                scores: item.value.scoring.value.scores
              })
            };
          }
          break;
        case 'orderedResponse':
          {
            this.scoring = {
              kind: item.value.scoring.kind,
              value: makeOrderedResponse({
                answerPermsScores: item.value.scoring.value.answerPermsScores,
                defaultScore: item.value.scoring.value.defaultScore
              })
            };
          }
          break;
        case 'submitted':
        case 'embedded': {
          this.scoring = { kind: item.value.scoring.kind };
        }
      }
    } else {
      this.content = initI18nEmpty(this.content);
      this.category = { kind: 'skill' };
      if (skill !== undefined) {
        this.skill = skill;
      } else {
        /// No skill passed in either in item or separately show filters
        this.showFilters = true;
      }
    }
  }

  skillFromId = (skillId: DbKey<Skill>): WithDbId<Skill> | undefined => {
    const { competencySkillsMap } = this.props;
    /// Find skill and set up
    for (const skills of competencySkillsMap.skillsByCompetency.values()) {
      const itemSkill = skills.find(sk => sk.id === skillId);
      /// At least one should fit... We have issues if the item doesn't have one or it's not in the list
      if (itemSkill) {
        return itemSkill;
      }
    }
    return undefined;
  };

  @action
  changeLang = (opt: SelectOption) => {
    this.lang = opt.value as LangCode;
  };

  @action lockUpdates = () => (this.locked = true);

  @action
  changeAnswerType = (opt?: SelectOption) => {
    if (!opt) return;
    const newType = opt.value as AnswerType;
    // Causes a change in the rest of the scoring...
    // single/multi -> ordered: scores -> answerPermsScores (throw away existing)
    // multi -> single/ordered: set defaultScore
    // ordered -> single/multi: answerPermsScores -> scores (throw away existing)
    switch (this.scoring.kind) {
      case 'singleResponse':
        switch (newType) {
          case 'orderedResponse':
            this.scoring = {
              kind: 'orderedResponse',
              value: makeOrderedResponse({
                answerPermsScores: [],
                defaultScore: this.scoring.value.defaultScore
              })
            };
            break;
          case 'multiResponse':
            this.scoring = {
              kind: 'multiResponse',
              value: makeMultiResponse({
                scores: this.scoring.value.scores
              })
            };
            break;
          case 'submitted':
            this.scoring = { kind: 'submitted' };
            break;
          case 'embedded':
            this.scoring = { kind: 'embedded' };
        }
        break;
      case 'multiResponse':
        switch (newType) {
          case 'orderedResponse':
            this.scoring = {
              kind: 'orderedResponse',
              value: makeOrderedResponse({
                answerPermsScores: [],
                defaultScore: 33
              })
            };
            break;
          case 'singleResponse':
            this.scoring = {
              kind: 'singleResponse',
              value: makeSingleResponse({
                ...this.scoring.value,
                defaultScore: 33
              })
            };
            break;
          case 'submitted':
            this.scoring = { kind: 'submitted' };
            break;
          case 'embedded':
            this.scoring = { kind: 'embedded' };
        }
        break;
      case 'orderedResponse':
        switch (newType) {
          case 'singleResponse':
            this.scoring = {
              kind: 'singleResponse',
              value: makeSingleResponse({
                scores: [],
                defaultScore: this.scoring.value.defaultScore
              })
            };
            break;
          case 'multiResponse':
            this.scoring = {
              kind: 'multiResponse',
              value: makeMultiResponse({
                scores: []
              })
            };
            break;
          case 'submitted':
            this.scoring = { kind: 'submitted' };
            break;
          case 'embedded':
            this.scoring = { kind: 'embedded' };
        }
        break;
      case 'submitted':
      case 'embedded':
        switch (newType) {
          case 'singleResponse':
            this.scoring = {
              kind: 'singleResponse',
              value: makeSingleResponse({
                scores: [],
                defaultScore: 33
              })
            };
            break;
          case 'multiResponse':
            this.scoring = {
              kind: 'multiResponse',
              value: makeMultiResponse({
                scores: []
              })
            };
            break;
          case 'orderedResponse':
            this.scoring = {
              kind: 'orderedResponse',
              value: makeOrderedResponse({
                answerPermsScores: [],
                defaultScore: 33
              })
            };
        }
    }
  };

  // TODO: Combine all of these on... actions into one update as they are related
  // (to prevent infinite loops)
  @action
  onFilterCompetency = (selectedCompetency: SelectOption) => {
    selectedCompetency.value !== ''
      ? (this.filterCompetencyId = `${selectedCompetency.value}`)
      : (this.filterCompetencyId = undefined);
    // Clear saved skill
    this.skill = undefined;
    // and update skill options
    this.skillOptions = this.setSkillOptions();
  };

  @action
  onFilterSkill = (selectedSkill: SelectOption) => {
    selectedSkill.value !== '' ? (this.skill = this.skillFromId(`${selectedSkill.value}`)) : (this.skill = undefined);
    // (selectedSkill.value !== "")
    //   ? this.selectedSkillId = `${selectedSkill.value}`
    //   : this.selectedSkillId = undefined
  };

  @action
  onSelectQuestionCategory = (selectedQuestionCategory: SelectOption) => {
    this.category = this.stringToQuestionCategory(selectedQuestionCategory.value as string);
    if (this.category.kind === 'skill') {
      this.answerTypeOptions = this.skillCategoryAnswerTypeOptions;
    } else {
      this.answerTypeOptions = this.nonSkillCategoryAnswerTypeOptions;
      switch (this.category.value.kind) {
        case 'embeddedOffering':
          this.changeAnswerType(this.answerTypeOptions.find(opt => opt.value === 'embedded'));
          break;
        default:
          this.changeAnswerType(this.answerTypeOptions.find(opt => opt.value === 'submitted'));
      }
    }
  };

  @action
  setCompetencyOptions = (): SelectOption[] => {
    const { competencySkillsMap } = this.props;
    const { competencies } = competencySkillsMap;
    const options: SelectOption[] = [];
    /// TODO Internationalization
    competencies.forEach((v, k) => options.push({ label: v.fullName.value.en, value: k }));
    // Sort
    options.sort((a, b) => a.label.localeCompare(b.label));
    return [{ label: 'None', value: '' }, ...options];
  };

  @action
  setSkillOptions = (): SelectOption[] => {
    const { competencySkillsMap } = this.props;
    const { skillsByCompetency } = competencySkillsMap;
    const options: SelectOption[] = [];
    const skills = this.filterCompetencyId && skillsByCompetency.get(this.filterCompetencyId);
    /// TODO Internationalization
    if (skills === undefined || skills === '') {
      skillsByCompetency.forEach((v, _k) =>
        v.map(skill => options.push({ label: skill.value.name.value.en, value: skill.id }))
      );
    } else {
      skills.map(skill => options.push({ label: skill.value.name.value.en, value: skill.id }));
    }
    // Sort
    options.sort((a, b) => a.label.localeCompare(b.label));
    return [{ label: 'None', value: '' }, ...options];
  };

  @action
  setQuestionCategoryOptions = (): SelectOption[] => {
    return [
      { label: 'Standard Question (linked to Skill)', value: this.questionCategoryToString({ kind: 'skill' }) },
      {
        label: 'Introduction: Information Only',
        value: this.questionCategoryToString({
          kind: 'introduction', value: { kind: 'information' }
        })
      },
      // { 
      //   label: 'Introduction: Embedded Offering', 
      //   value: this.questionCategoryToString({
      //     kind: 'introduction', value: { kind: 'embeddedOffering', value: '#TODO' } 
      //   }) 
      // },
      {
        label: 'Conclusion: Information Only',
        value: this.questionCategoryToString({
          kind: 'conclusion', value: { kind: 'information' }
        })
      },
      // { 
      //   label: 'Conclusion: Embedded Offering',
      //   value: this.questionCategoryToString({
      //     kind: 'conclusion', value: { kind: 'embeddedOffering', value: '#TODO'}
      //   })
      // }
    ];
  };

  questionCategoryToString = (cat: QuestionCategory): string => {
    switch (cat.kind) {
      case 'skill':
        return 'skill';
      // FIXME:
      case 'introduction':
        return 'introduction-information';
      case 'conclusion':
        return 'conclusion-information';
    }
  };

  stringToQuestionCategory = (value: string): QuestionCategory => {
    switch (value) {
      // FIXME:
      case 'conclusion-information':
        return { kind: 'conclusion', value: { kind: 'information' } };
      case 'introduction-information':
        return { kind: 'introduction', value: { kind: 'information' } };
      default:
        return { kind: 'skill' };
    }
  };

  @computed
  get competencyOption(): SelectOption | undefined {
    return this.filterCompetencyId
      ? this.competencyOptions.find(option => option.value === this.filterCompetencyId)
      : undefined;
  }

  @computed
  get skillOption(): SelectOption | undefined {
    return this.skillOptions.find(option => option.value === this.filteredSkillOption.value);
  }

  @computed
  get questionCategoryOption(): SelectOption {
    const defaultResult = {
      label: 'Standard Question (linked to Skill)',
      value: this.questionCategoryToString({ kind: 'skill' })
    };
    const currentValue = this.questionCategoryToString(this.category);
    const result = this.questionCategoryOptions.find(opt => opt.value === currentValue);
    return result || defaultResult;
  }

  @computed
  get filteredSkillOption(): SelectOption {
    const skillId = this.skill ? this.skill.id : undefined;
    const option = skillId ? this.skillOptions.find(opt => opt.value === skillId) : undefined;
    return option === undefined || this.skill === undefined ? { label: 'Select...', value: '' } : option;
  }

  // There's only one questions folder - but may be more later (per client)
  // Also just keeping things consistent, and using consistent way of picking
  // repo name.
  getRepo = (): RepositoryEntries => {
    return FileUtility.getQuestionsRepo(this.props.repository);
  };

  LangSelector = observer(() => {
    const { i18nOptions } = this.props;
    const onChange = this.changeLang;
    const options = i18nOptions();
    return (
      <SelectBox
        options={options}
        value={options.find(option => (option.value as LangCode) === this.lang)}
        onChange={onChange}
        xsSpan={1}
        title={'Language'}
      />
    );
  });

  AnswerTypeSelector = observer(() => {
    const options = this.answerTypeOptions;
    const onChange = this.changeAnswerType;
    return (
      <SelectBox
        options={options}
        value={options.find(option => (option.value as AnswerType) === this.scoring.kind)}
        onChange={onChange}
        xsSpan={2}
        title={'Answer Type'}
        // isDisabled={this.category !== QuestionCategory.skill}
      />
    );
  });

  NameObserver = observer(() =>
    TextInputObserver({
      field: this.name,
      type: 'text',
      title: 'Question Name',
      placeholder: '',
      xsSpan: 4,
      debug: this.props.debug
    })
  );

  ContentObserver = observer(() =>
    I18nMarkdownEditorObserver({
      title: 'Question Content',
      field: this.content,
      lang: this.lang,
      repo: this.getRepo(),
      onUpload: this.props.onUpload,
      xsSpan: 4
    })
  );

  @computed
  get inputIsValid(): boolean {
    /// TODO orderedResponse: Every <AnswerIndex[], Score> pair must be different
    const { isI18nValid } = this.props;

    let valid = true;

    // valid = this.category !== undefined // && this.skill !== undefined

    /// Skill-specific requirements only apply to Skill questions.
    /// Introduction/Conclusion (Information Only) questions have no skill,
    /// answers, or scoring requirements.
    if (this.category.kind === 'skill') {
      /// Check skill is set for Skill questions
      if (valid) {
        valid = !!this.skill;
      }

      /// Check scoring is correct for Skill questions
      if (valid) {
        valid = !['submitted', 'embedded'].includes(this.scoring.kind);
      }

      if (valid) {
        /// If orderedResponse, and there are scores, they must all be different
        if (this.scoring.kind === 'orderedResponse') {
          const answerPerms = this.scoring.value.answerPermsScores.map(aps => aps.v1);
          answerPerms.forEach((ap, idx) => {
            const apTest = ap;
            if (answerPerms.some((v, i) => v.every((x, j) => x === apTest[j]) && i !== idx)) {
              valid = false;
            }
          });
        }
      }
    }

    return valid && this.name.getValid() !== undefined && isI18nValid(this.content);
  }

  onSave = async () => {
    const { item, makeI18nContent, onCreate, onUpdate } = this.props;
    let question;
    if (this.skill && this.category.kind === 'skill') {
      question = makeQuestion({
        name: this.name.value(),
        skillId: this.skill.id,
        category: this.category,
        content: makeI18nContent(this.content),
        answers: this.answers.map(ans => ans.answer.value),
        scoring: this.scoring
      });
    } else if (this.category.kind !== 'skill') {
      question = makeQuestion({
        name: this.name.value(),
        /// Set explicitly
        skillId: null,
        category: this.category,
        content: makeI18nContent(this.content),
        answers: this.answers.map(ans => ans.answer.value),
        /// Set explicitly
        scoring: { kind: 'submitted' }
      });
    }

    if (question) {
      if (item && onUpdate) {
        await onUpdate(
          makeWithDbId({
            id: item.id,
            value: { ...question }
          })
        );
      } else {
        await onCreate(question);
      }
    }
  };

  @action
  onAnswerUpdates = (newAnswers: InputAnswer<I18nContent>[]) => {
    /// Check if reordered or deleted - if so, update the scores as well
    /// Ignore if added *unless* the new answer is a 'Not Applicable' response,
    /// which we set to the score zero.
    switch (this.scoring.kind) {
      case 'singleResponse':
      case 'multiResponse':
        {
          const updateScores: Pair<AnswerIndex, Score>[] = [];

          const existingScores = this.scoring.value.scores.slice();
          newAnswers.forEach((ans, idx) => {
            /// Find index in existing answers
            const existingIdx = this.answers.findIndex(a => a.answer.id === ans.answer.id);
            /// If it's in there, check whether it's reordered
            if (existingIdx >= 0) {
              const existingScore = existingScores.find(score => score.v1 === existingIdx);
              if (existingScore) {
                if (existingIdx !== idx) {
                  /// moved find existing scores and store against new index
                  updateScores.push(makePair({ v1: idx, v2: existingScore.v2 }));
                } else {
                  /// Not moved just replicate
                  updateScores.push(existingScore);
                }
              }
              /// else existing answer but no existing score... Could happen if no score yet set
              /// against answer. Either way, ignore
            } else {
              /// New answer without existing score check if 'Not Applicable'
              if (ans.isNotApplicableAnswer) {
                /// Store zero value by default
                updateScores.push(makePair({ v1: idx, v2: 0 }));
              }
            }
          });

          /// Update scores
          this.scoring.value.scores = updateScores;
        }
        break;
      /// Currently NotApplicable answers not implemented...
      case 'orderedResponse': {
        const allUpdateScores: Pair<AnswerIndex[], Score>[] = [];
        let returnEmpty = false;

        /// First check if an answer has been deleted - this invalidates all existing answers
        this.answers.forEach(ans => {
          const answerId = ans.answer.id;
          /// Find index in new answers
          const oldIdx = newAnswers.findIndex(a => a.answer.id === answerId);
          if (oldIdx < 0) {
            /// Answer deleted Return empty array
            returnEmpty = true;
          }
        });

        /// Do the same for newAnswers (answer added)
        newAnswers.forEach(ans => {
          const answerId = ans.answer.id;
          /// Find index in new answers
          const newIdx = this.answers.findIndex(a => a.answer.id === answerId);
          if (newIdx < 0) {
            /// Answer added Return empty array
            returnEmpty = true;
          }
        });

        if (!returnEmpty) {
          /// Answers unchanged check if re-ordered
          this.scoring.value.answerPermsScores.forEach(score => {
            const updatedPerm: AnswerIndex[] = [];
            /// v1 = indexes of existing answers in order
            score.v1.forEach(idx => {
              const answerId = this.answers[idx].answer.id;
              /// Find index in new answers
              const newIdx = newAnswers.findIndex(ans => ans.answer.id === answerId);
              /// If found, update scoring
              if (newIdx >= 0) {
                updatedPerm.push(newIdx);
              }
              /// If not found, answer has been deleted drop out
            });
            allUpdateScores.push(makePair({ v1: updatedPerm, v2: score.v2 }));
          });
          /// Update scores
          this.scoring.value.answerPermsScores = allUpdateScores;
        } else {
          this.scoring.value.answerPermsScores = [];
        }
      }
    }

    /// Update answers
    this.answers = newAnswers;
  };

  @action
  onScoringUpdates = (newScores: AnswerScores) => {
    /// Check if 'notApplicable' scores updated to non-zero if so, clear the flag on answers
    switch (newScores.kind) {
      case 'singleResponse':
      case 'multiResponse': {
        this.answers
          .filter(ans => ans.isNotApplicableAnswer)
          .forEach(ans => {
            const answerIdx = this.answers.findIndex(a => a.answer.id === ans.answer.id);
            const existingScore = newScores.value.scores.find(score => score.v1 === answerIdx);
            if (existingScore && existingScore.v2 !== 0) {
              this.answers[answerIdx].isNotApplicableAnswer = false;
            }
          });
      }
    }
    this.scoring = newScores;
  };

  /// SelectBox rather than SelectObserver - as latter is nt actually an observer
  CompetencyFilter = observer(() => {
    return (
      <SelectBox
        options={this.competencyOptions}
        defaultValue={this.competencyOption}
        onChange={this.onFilterCompetency}
        xsSpan={2}
        title={'Competency'}
        isDisabled={this.category.kind !== 'skill'}
      />
    );
  });

  /// SelectBox rather than SelectObserver - as latter is nt actually an observer
  SkillFilter = observer(() => {
    return (
      <SelectBox
        options={this.skillOptions}
        defaultValue={this.skillOption}
        onChange={this.onFilterSkill}
        xsSpan={2}
        title={'Skill'}
        isDisabled={this.category.kind !== 'skill'}
      />
    );
  });

  QuestionCategorySelector = observer(() => {
    return (
      <SelectBox
        options={this.questionCategoryOptions}
        value={this.questionCategoryOption}
        onChange={this.onSelectQuestionCategory}
        xsSpan={4}
        title={'Quiz Question Category'}
      />
    );
  });

  AnswersEditor = () => {
    const { debug, makeI18nContent, onCancel } = this.props;
    return (
      <AnswersEditor
        debug={debug}
        lang={this.lang}
        name={this.name.value()}
        content={makeI18nContent(this.content)}
        answers={this.answers}
        answerType={this.scoring.kind}
        postUpdates={this.onAnswerUpdates}
        onCancel={onCancel}
      />
    );
  };

  ScoringEditor = (scoring: AnswerScores) => {
    const { debug, makeI18nContent, onCancel } = this.props;
    switch (scoring.kind) {
      case 'singleResponse': {
        return (
          <SingleResponseEditor
            debug={debug}
            lang={this.lang}
            name={this.name.value()}
            content={makeI18nContent(this.content)}
            answers={this.answers.map(ans => ans.answer)}
            scoring={scoring}
            postUpdates={this.onScoringUpdates}
            onCancel={onCancel}
          />
        );
      }
      case 'multiResponse': {
        return (
          <MultiResponseEditor
            debug={debug}
            lang={this.lang}
            name={this.name.value()}
            content={makeI18nContent(this.content)}
            answers={this.answers.map(ans => ans.answer)}
            scoring={scoring}
            postUpdates={this.onScoringUpdates}
            onCancel={onCancel}
          />
        );
      }
      case 'orderedResponse': {
        return (
          <OrderedResponseEditor
            debug={debug}
            lang={this.lang}
            name={this.name.value()}
            content={makeI18nContent(this.content)}
            answers={this.answers.map(ans => ans.answer)}
            scoring={scoring}
            postUpdates={this.onScoringUpdates}
            onCancel={onCancel}
          />
        );
      }
      default:
        return null;
    }
  };

  Edit = () => {
    const skillName = this.skill ? this.skill.value.name.value.en : '';
    const skillRow = this.showFilters ? (
      <>
        <div className={styles.row}>
          <this.QuestionCategorySelector />
        </div>
        <div className={styles.row}>
          <this.CompetencyFilter />
          <this.SkillFilter />
        </div>
      </>
    ) : (
      <div className={styles.row}>Skill: {skillName}</div>
    );
    return (
      <div className={styles.page}>
        <div className={styles.body}>
          {skillRow}
          <div className={styles.row}>
            <this.AnswerTypeSelector />
            <this.LangSelector />
          </div>
          <div className={styles.row}>
            <this.NameObserver />
          </div>
          <div className={styles.row}>{<this.ContentObserver />}</div>
        </div>
      </div>
    );
  };

  /// Annoyingly, we have to pull the guts out of QuizQuestion to avoid rewriting the PageLayout
  /// TODO: refactor this code out of QuizQuestion itself
  renderOrderedAnswersPreview(): JSX.Element {
    const { t } = this.props;
    // Default order on first render
    const draggableAnswers: DraggableOption[] = this.answers.map(ac => {
      return {
        id: ac.answer.id,
        text: t(ac.answer.value)
      };
    });
    return <DnDContainer draggableOptions={draggableAnswers} onMove={fakeCallback('on move')} />;
  }

  renderSelectionAnswersPreview(): JSX.Element[] {
    return this.answers.map(ac => this.renderAnswerContentPreview(ac.answer));
  }

  renderAnswerContentPreview = (answer: WithId<I18nContent>): JSX.Element => {
    const { t } = this.props;
    switch (this.scoring.kind) {
      case 'singleResponse': {
        return this.renderSingleAnswerContentPreview(t(answer.value, this.lang), answer.id);
      }
      case 'multiResponse':
      case 'submitted':
      case 'embedded': {
        return this.renderMultiAnswerContentPreview(t(answer.value, this.lang), answer.id);
      }
      default:
        return <div>Answer type not implemented</div>;
    }
  };

  renderSingleAnswerContentPreview = (answerContent: string, i: number): JSX.Element => {
    return (
      <div key={i}>
        <RadioItem size={'xxsmall'} label={answerContent} selected={false} onChange={fakeCallback('on radio select')} />
      </div>
    );
  };

  renderMultiAnswerContentPreview = (answerContent: string, i: number): JSX.Element => {
    return (
      <div key={i}>
        <CheckItem
          size={'xxsmall'}
          label={answerContent}
          selected={false}
          onChange={fakeCallback('on checkItem select')}
        />
      </div>
    );
  };

  FakeQuizQuestion = () => {
    const { makeI18nContent, t } = this.props;
    return (
      <div className={questionScreenStyles.container}>
        <div className={questionScreenStyles.content}>
          <div className={questionScreenStyles.exit}>
            <CloseIcon size={'xsmall'} onClick={fakeCallback('on close icon click')} />
          </div>
          <div className={questionScreenStyles.title}>{`Question m of n`}</div>
          <div className={questionScreenStyles.question}>
            <MarkdownContent text={t(makeI18nContent(this.content), this.lang)} />
          </div>
          <div className={questionScreenStyles.answers}>
            {this.scoring.kind === 'orderedResponse'
              ? this.renderOrderedAnswersPreview()
              : this.renderSelectionAnswersPreview()}
          </div>
          <div className={questionScreenStyles.buttons}>
            <Button
              onClick={fakeCallback('on save and close')}
              variant={'tertiary'}
              className={questionScreenStyles.button}
            >
              {i18next.t('question.save-close', 'Save and Close')}
            </Button>
            <Button onClick={fakeCallback('on next')} disabled={true} className={questionScreenStyles.button}>
              {i18next.t('question.next', 'Next')}
            </Button>
          </div>
        </div>
      </div>
    );
  };

  Preview = () => {
    return (
      <div className={styles.page}>
        <div className={styles.body}>
          <div className={styles.row}>
            <h4>{this.name.value()}</h4>
            <this.LangSelector />
          </div>
        </div>
        <this.FakeQuizQuestion />
      </div>
    );
  };

  render() {
    const { item, onCancel, onUpdate } = this.props;
    const title1 = (item && onUpdate ? 'Update' : 'New') + ' Question';
    const title2 = 'Answers';
    const title3 = 'Scoring';
    const title4 = 'Preview';
    const notSkillQuestion =
      this.skill === undefined ||
      this.category.kind !== 'skill' ||
      (item !== undefined && item.value.category.kind !== 'skill');
    return (
      // {/* TODO aria (or DOM) labels */}
      <TabContainer
        nonTabContent={renderButtons(
          this.onSave,
          undefined,
          onCancel,
          () => this.inputIsValid && !this.locked,
          () => false,
          () => !this.locked
        )}
        items={[
          {
            key: 0,
            title: title1,
            content: <this.Edit />,
            selected: true
          },
          {
            key: 1,
            title: title2,
            content: <this.AnswersEditor />,
            selected: false,
            disabled: isEmpty(this.content.en) // || notSkillQuestion
          },
          {
            key: 2,
            title: title3,
            content: this.ScoringEditor(this.scoring),
            selected: false,
            disabled: isEmpty(this.content.en) || this.answers.length === 0 || notSkillQuestion
          },
          {
            key: 3,
            title: title4,
            content: <this.Preview />,
            selected: false
            //        disabled: false //!this.inputIsNonEmpty()
          }
        ]}
        selectedTabKey={0}
      />
    );
  }
}

export interface QuestionDeleterProps {
  items?: WithDbId<Question>[];
  onDelete?(ids: DbKey<Question>[]): Promise<void>;
  onCancel(): void;
}

/** Base component to delete Skill */
export class QuestionDeleter extends React.Component<QuestionDeleterProps> {
  onDelete = async () => {
    const { items, onDelete } = this.props;
    if (items && onDelete) {
      await onDelete(items.map(item => item.id));
    }
  };

  render() {
    const { onCancel, items } = this.props;
    return items ? (
      <div className={styles.page}>
        <div className={styles.body}>
          <div className={styles.row}>
            {deleteHandler(this.onDelete, onCancel, 'Questions', items.map(item => item.value.name))}
          </div>
        </div>
      </div>
    ) : null;
  }
}

export const QuestionAnswerEditor = withI18n(QuestionAnswerEditor0);



// WEBPACK FOOTER //
// ./src/admin/ui/components/editors/question-answer-editor/question-answer-editor.tsx